import { useEffect, useRef, useState } from 'react';
import { MAX_CHAT_MESSAGE, chatArrived, chatFollows, chatHushed } from '@versesverse/core';
import type { ChatChannel, ChatMessage } from '@versesverse/core';
import { useLang } from '../i18n/lang';

export interface RoomChatProps {
  /**
   * SOMETHING THAT CAN BE TYPED INTO — not a room, and not a voice session.
   *
   * This asked for a whole `VoiceSession`, which made the panel unusable by any chat that is not
   * attached to a live audio room. §8.1 says most of them will not be.
   */
  chat: ChatChannel;
  meId: string | null;
  /**
   * Whom this reader has turned down for themselves — identities, from the room above.
   *
   * The hush was audio only, so somebody a reader could not bear to listen to went on reaching them
   * in text, in the same panel, at the same moment. "Silence them for me" has to mean silence.
   */
  hushed: string[];
}



/*
 * TEXT, INSIDE THE ROOM (D12).
 *
 * It rides the voice provider's data channel, which the join token already grants — so live chat
 * needs no WebSocket gateway, no presence service, no Redis backplane and no scaling story of its
 * own. That is the whole reason `SOCIAL-PLANE.md §4` put live text *here* rather than building a
 * third always-on layer beside the discussion.
 *
 * IT IS EPHEMERAL, and that is the design rather than a shortcut. What is said in a room stays in
 * the room; the durable record of what was said about a node is the discussion, which is a
 * different product with a different lifetime. Nothing here is persisted anywhere, and when the
 * room collapses (D23) it is gone with it.
 *
 * Everyone present may type, listeners included — a room where only the stage can speak is a panel,
 * but one where only the stage can *type* is a broadcast, and questions from the floor are the
 * reason a stage exists.
 */
export function RoomChat({ chat, meId, hushed }: RoomChatProps): React.JSX.Element {
  const { t } = useLang();
  const [messages, setMessages] = useState<readonly ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [notSent, setNotSent] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  /*
   * WHETHER THE READER WAS AT THE END, recorded WHEN THEY SCROLL rather than worked out afterwards.
   *
   * By the time the effect below runs, the new message is already in the list and its height is
   * already in `scrollHeight` — so asking then would measure a distance that includes the message
   * we are deciding about, and a reader sitting at the end would look scrolled up by exactly one
   * row. The only moment the question has a clean answer is the last time the reader moved.
   *
   * Starts true: an empty list is at its end, and the first message must arrive in view.
   */
  const following = useRef(true);

  /*
   * A REF, so hushing somebody does not resubscribe the data channel.
   *
   * The subscription closes over whatever it was given; re-running it on every change to `hushed`
   * would tear down and rebuild the channel listener while a room is talking, which is a message
   * lost for everybody else in exchange for one reader's button.
   */
  const hushedNow = useRef(hushed);
  useEffect(() => {
    hushedNow.current = hushed;
    /* And what they already said goes with them — `chatHushed`, which states why, and why the
       same list comes back when nothing went. */
    setMessages(current => chatHushed(current, hushed));
  }, [hushed]);

  /*
   * WHAT ARRIVES, AND WHAT IS DROPPED — decided by `chatArrived` rather than here.
   *
   * How many are held and whose words are dropped on arrival are rules every chat keeps, not this
   * panel's private business (§8.1). What stays here is the subscription itself.
   */
  useEffect(() => chat.onMessage(message => {
    setMessages(current => chatArrived(current, message, hushedNow.current));
  }), [chat]);

  /* Follow the conversation — for the reader who is following it. `chatFollows` says why that is
     a question rather than an always. Guarded because jsdom has no layout of its own. */
  useEffect(() => {
    const list = listRef.current;
    if (list && following.current) list.scrollTop = list.scrollHeight;
  }, [messages]);

  const send = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    /* Cleared first: the send settles over a data channel, and leaving the box full while it does
       makes a reader send twice. */
    setDraft('');
    setNotSent(false);
    try {
      await chat.sendMessage(text);
    } catch {
      /*
       * AND GIVEN BACK WHEN IT DID NOT GO. The words are the reader's; losing them is the harm.
       *
       * This awaited a promise nobody caught, so a refused publish cleared the box, threw into
       * nowhere, and left a reader who had typed a question in a room of three hundred with no
       * question, no message on screen and no reason to think anything had gone wrong.
       */
      setDraft(text);
      setNotSent(true);
    }
  };

  return (
    <section className="room-chat" aria-label={t('chat.label')}>
      <h4 className="room-chat-heading">{t('chat.heading')}</h4>
      {notSent && <p className="room-chat-problem" role="alert">{t('chat.notSent')}</p>}

      {/* A live region so a screen reader hears arrivals without the canvas or the roster. */}
      <ul
        className="room-chat-list"
        ref={listRef}
        aria-live="polite"
        aria-relevant="additions"
        onScroll={event => {
          const list = event.currentTarget;
          following.current = chatFollows(list.scrollHeight - list.scrollTop - list.clientHeight);
        }}
      >
        {messages.length === 0 ? (
          /*
           * "SINCE YOU ARRIVED", because "nothing said yet" was false for most of the people who
           * read it.
           *
           * A room's chat is ephemeral by design and rides the data channel (D12, D23): there is no
           * history, and a reader joining an hour-old conversation sees an empty list. They were
           * being told nothing had been said in a room where a great deal had — the same shape as a
           * failed read reported as an empty one, with the reader's own arrival in place of the
           * failure. This sentence is true for them and true for whoever opened the room, and it
           * says quietly that what came before is not kept.
           */
          <li className="room-chat-empty">{t('chat.empty')}</li>
        ) : messages.map(message => (
          <li
            key={message.id}
            className={message.identity === meId ? 'room-chat-message mine' : 'room-chat-message'}
          >
            <span className="room-chat-who">{message.name}</span>
            {/* Rendered as text, never markup — this is another participant's browser talking. */}
            <span className="room-chat-text">{message.text}</span>
          </li>
        ))}
      </ul>

      <form className="room-chat-compose" onSubmit={event => void send(event)}>
        <input
          type="text"
          className="room-chat-input"
          value={draft}
          onChange={event => setDraft(event.target.value)}
          placeholder={t('chat.placeholder')}
          aria-label={t('chat.placeholder')}
          maxLength={MAX_CHAT_MESSAGE}
        />
        <button type="submit" className="room-chat-send" disabled={!draft.trim()}>
          {t('chat.send')}
        </button>
      </form>
    </section>
  );
}
